import { useEffect, useMemo, useState, useRef } from 'react';
import axios from '../plugins/axios'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import lowSound from '../assets/sounds/IEC_low.wav';
import medSound from '../assets/sounds/IEC_med.wav';
import highSound from '../assets/sounds/IEC_high.wav';
import ReminderSound from '../assets/sounds/Reminder.wav';
import { alarmStateFamily, audioPlayState, alarmsState, globalAlarmState, bedInfoState, silenceStateFamily, acknowledgeStateFamily, wardSuspendState } from '../plugins/recoil';


class AlarmSoundManager {
    constructor() {
        this.audioInstances = {
            high: new Audio(highSound),
            med: new Audio(medSound),
            low: new Audio(lowSound),
            reminder: new Audio(ReminderSound),
        };
        this.queue = []; // 우선순위 큐
        this.silencedSerial = [];
        this.silenceRemainingMap = new Map(); // Rodney 추가 - serial -> remainingSeconds
        this.acknowledgedAlarms = this.loadAcknowledgedAlarms();
        this.currentPlaying = null; // 현재 재생 중인 알람 타입
        this.currentTimer = null; // 재생/대기 타이머
        this.isPlaying = false; // 재생 중 여부
        this.holdNext = false;
        this.queueRetryTimer = null;
        this.cleanupTimers = new Map();

        this.lastFinishedType = null;
        this.cooldownUntil = 0;
        
        // 전역 알람 상태 관리
        this.allBedsAlarms = new Map(); // serial -> alarms 맵핑
        this.allBedsInfo = new Map(); // serial -> bedInfo 맵핑
        this.allBedsData = new Map();

        // force 재생 중복 방지용
        this.lastForceReplay = { key: null, at: 0 };

        // ✅ Reminder 관련 상태를 클래스 내부로 이동
        this._reminderIntervalId = null;
        this._reminderPlayAudio = false;
        this._reminderWardSuspend = '0';
        this._reminderEvalTimer = null;
    }

    // 실제 오디오가 현재 재생중인지 확인
    isAudioActuallyPlaying(soundType) {
        const a = this.audioInstances[soundType];
        // currentTime > 0 조건 제거 (play 직후 오판 방지)
        return !!(a && !a.paused && !a.ended);
    }

    getAudibleType() {
        const types = ['high', 'med', 'low'];
        return types.find((t) => this.isAudioActuallyPlaying(t)) || null;
    }

    clearCooldown() {
        this.lastFinishedType = null;
        this.cooldownUntil = 0;
    }

    getPlaybackConfig(soundType) {
        switch (soundType) {
            case 'high':
                return { playCount: 1, interval: 1400, stopTime: 13000 };
            case 'med':
                return { playCount: 1, interval: 1000, stopTime: 15000 };
            case 'low':
                return { playCount: 1, interval: 1000, stopTime: 30000 };
            default:
                return null;
        }
    }

    // Rodney - CMS에서 120초 Slience 타이머 남은 시간 업데이트 하기 위해 추가
    updateSilenceRemaining(serial, remaining) {
        if (remaining > 0) {
            this.silenceRemainingMap.set(serial, remaining);
        } else {
            this.silenceRemainingMap.delete(serial);
        }
    }

    // 침상별 알람 상태 업데이트
    updateBedAlarms(serial, alarms, bedInfo, bedData) {
        this.allBedsAlarms.set(serial, alarms || []);
        this.allBedsInfo.set(serial, bedInfo);
        this.allBedsData.set(serial, bedData);
    }

    // 침상 알람 제거 (침상이 사라질 때)
    removeBedAlarms(serial) {
        this.allBedsAlarms.delete(serial);
        this.allBedsInfo.delete(serial);
        this.allBedsData.delete(serial);
    }

    // 전역적으로 모든 침상 중 가장 높은 우선순위 알람 찾기
    getHighestPriorityAlarmAcrossAllBeds() {
        const priorityOrder = { High: 1, Medium: 2, Low: 3, Normal: 4 };
        let highestAlarm = null;
        let highestSerial = null;
        let allSerialsWithSamePriority = [];

        this.allBedsAlarms.forEach((alarms, serial) => {
            const bedInfo = this.allBedsInfo.get(serial);
            const bedData = this.allBedsData.get(serial);

            // 비활성 침상은 제외
            if (!bedInfo || bedInfo.suspended || this.silencedSerial.includes(serial)) {
                return;
            }

            if (bedData?.silenceData) {
                const { silenceMode, alarmPause, audioPause } = bedData.silenceData;
                // silenceMode가 '1'이거나, alarmPause나 audioPause가 '1'이면 제외
                if (silenceMode === '1' || alarmPause === '1' || audioPause === '1') {
                    return;
                }
            }

            // unacknowledged 알람만 필터링
            const unacknowledgedAlarms = alarms.filter(alarm => 
                !this.isAlarmAcknowledged(serial, alarm.id)
            );

            if (unacknowledgedAlarms.length === 0) return;

            // 해당 침상의 가장 높은 우선순위 알람
            const bedHighestAlarm = unacknowledgedAlarms.reduce((prev, curr) =>
                priorityOrder[curr.type] < priorityOrder[prev.type] ? curr : prev,
                unacknowledgedAlarms[0]
            );

            // 전체 중 가장 높은 우선순위와 비교
            if (!highestAlarm || priorityOrder[bedHighestAlarm.type] < priorityOrder[highestAlarm.type]) {
                highestAlarm = bedHighestAlarm;
                highestSerial = serial;
                allSerialsWithSamePriority = [serial];
            } else if (highestAlarm && priorityOrder[bedHighestAlarm.type] === priorityOrder[highestAlarm.type]) {
                // 같은 우선순위인 경우 serial 추가
                allSerialsWithSamePriority.push(serial);
            }
        });

        return highestAlarm ? {
            alarm: highestAlarm,
            serials: allSerialsWithSamePriority
        } : null;
    }

    loadAcknowledgedAlarms() {
        try {
            const stored = localStorage.getItem('acknowledgedAlarms');
            if (stored) {
                const data = JSON.parse(stored);
                const acknowledgedAlarms = new Map();
                
                // 만료된 항목 제거하고 유효한 항목만 복원
                Object.entries(data).forEach(([serial, info]) => {
                    const elapsed = Date.now() - info.startTime;
                    if (elapsed < 120000) { // 2분 이내인 경우만 유지
                        acknowledgedAlarms.set(serial, {
                            alarmIds: new Set(info.alarmIds),
                            startTime: info.startTime
                        });
                    }
                });
                
                return acknowledgedAlarms;
            }
        } catch (error) {
            console.warn('Failed to load acknowledged alarms from localStorage:', error);
        }
        return new Map();
    }

    saveAcknowledgedAlarms() {
        try {
            const data = {};
            this.acknowledgedAlarms.forEach((value, key) => {
                data[key] = {
                    alarmIds: Array.from(value.alarmIds),
                    startTime: value.startTime
                };
            });
            localStorage.setItem('acknowledgedAlarms', JSON.stringify(data));
        } catch (error) {
            console.warn('Failed to save acknowledged alarms to localStorage:', error);
        }
    }

    // 특정 침상의 현재 알람들을 acknowledge
    acknowledgeCurrentAlarms(serial, currentAlarmIds) {
        this.acknowledgedAlarms.set(serial, {
            alarmIds: new Set(currentAlarmIds),
            startTime: Date.now()
        });
        this.saveAcknowledgedAlarms();
    }

    cleanupAcknowledgedAlarms(serial, currentAlarmIds) {
        const ackInfo = this.acknowledgedAlarms.get(serial);
        if (!ackInfo) return;

        // 기존 cleanup 타이머가 있으면 취소
        // if (this.cleanupTimers.has(serial)) {
        //     clearTimeout(this.cleanupTimers.get(serial));
        // }

        // Rodney - 5초 딜레이 삭제 (alarm이 없어졌다가 나타나면 새로운 알람으로 인식하기 위해 수정)
        const cleanupTimer = setTimeout(() => {
            const currentAckInfo = this.acknowledgedAlarms.get(serial);
            if (!currentAckInfo) {
                this.cleanupTimers.delete(serial);
                return;
            }

            // 시간 만료 체크
            const elapsed = Date.now() - currentAckInfo.startTime;
            if (elapsed >= 120000) { // 2분 만료
                this.acknowledgedAlarms.delete(serial);
                this.saveAcknowledgedAlarms();
                // console.log(`Acknowledged alarms expired for ${serial}`);
                this.cleanupTimers.delete(serial);
                return;
            }

            // 현재 알람 ID와 비교하여 cleanup
            const currentAlarmIdSet = new Set(currentAlarmIds);
            const filteredAcknowledgedIds = new Set();
            
            currentAckInfo.alarmIds.forEach(alarmId => {
                if (currentAlarmIdSet.has(alarmId)) {
                    filteredAcknowledgedIds.add(alarmId);
                }
            });
            
            if (filteredAcknowledgedIds.size === 0) {
                this.acknowledgedAlarms.delete(serial);
                console.log(`All acknowledged alarms removed for ${serial} after 5s delay`);
            } else {
                this.acknowledgedAlarms.set(serial, {
                    alarmIds: filteredAcknowledgedIds,
                    startTime: currentAckInfo.startTime
                });
                console.log(`Updated acknowledged alarms for ${serial} after 5s delay`);
            }
            this.saveAcknowledgedAlarms();
            this.cleanupTimers.delete(serial);
        }, 0); // Rodney = 5초 딜레이 삭제 

        this.cleanupTimers.set(serial, cleanupTimer);
    }

    // acknowledge 상태 해제 시 cleanup 타이머도 정리
    clearAcknowledged(serial) {
        this.acknowledgedAlarms.delete(serial);
        // cleanup 타이머도 함께 정리
        if (this.cleanupTimers.has(serial)) {
            clearTimeout(this.cleanupTimers.get(serial));
            this.cleanupTimers.delete(serial);
        }
        this.saveAcknowledgedAlarms();
    }

    // 특정 알람이 acknowledge되었는지 확인
    isAlarmAcknowledged(serial, alarmId) {
        const ackInfo = this.acknowledgedAlarms.get(serial);
        return ackInfo ? ackInfo.alarmIds.has(alarmId) : false;
    }

    // acknowledge된 알람들이 있는 침상인지 확인
    // hasAcknowledgedAlarms(serial) {
    //     const ackInfo = this.acknowledgedAlarms.get(serial);
    //     return ackInfo && ackInfo.alarmIds.size > 0;
    // }    

    hasNewUnacknowledgedAlarm(serial, currentAlarmIds) {
        const ackInfo = this.acknowledgedAlarms.get(serial);
        if (!ackInfo) return true; // acknowledge된 적이 없으면 새로운 알람
        
        // 현재 알람 중 acknowledged되지 않은 것이 있는지 확인
        return currentAlarmIds.some(alarmId => !ackInfo.alarmIds.has(alarmId));
    }

    // acknowledge 시작 시간 가져오기
    getAcknowledgeStartTime(serial) {
        const ackInfo = this.acknowledgedAlarms.get(serial);
        return ackInfo ? ackInfo.startTime : null;
    }

    // 우선순위 정의
    getPriority(soundType) {
        const priorityOrder = { high: 1, med: 2, low: 3 };
        return priorityOrder[soundType] || 4;
    }

    checkHasAlreadyQueue(newType, serial) {
        // silence 타이머 동작 중이면 큐에 추가 안함
        if (this.silenceRemainingMap.get(serial) > 0) return true;

        const targetAlarm = this.queue.find(alarm => alarm.soundType === newType)
        if (targetAlarm) {
            const hasSerial = targetAlarm.serialList.indexOf(serial) !== -1
            if (!hasSerial) targetAlarm.serialList.push(serial)
            return true
        } else {
            return false
        }
    }

    addToQueue(soundType, playCount, interval, stopTime, serialList) {
        const filteredSerials = serialList.filter(
            serial => !(this.silenceRemainingMap.get(serial) > 0)
        );
        if (filteredSerials.length === 0) return;

        const existing = this.queue.find(item => item.soundType === soundType);
        if (existing) {
            existing.serialList = Array.from(new Set([...existing.serialList, ...filteredSerials]));
            existing.playCount = playCount;
            existing.interval = interval;
            existing.stopTime = stopTime;
        } else {
            this.queue.push({ soundType, playCount, interval, stopTime, serialList: filteredSerials });
            this.queue.sort((a, b) => this.getPriority(a.soundType) - this.getPriority(b.soundType));
        }

        // 실제 오디오가 재생 중이면 시작하지 않음 (겹침 방지)
        if (!this.isPlaying && !this.holdNext && !this.getAudibleType()) {
            this.processQueue();
        }
    }

    processQueue(needToWait) {
        if (this.holdNext) return;
        if (this.queue.length === 0) return;

        // 실제 오디오가 아직 끝나지 않았으면 짧게 재시도 예약
        if (this.isPlaying || this.getAudibleType()) {
            if (!this.queueRetryTimer) {
                this.queueRetryTimer = setTimeout(() => {
                    this.queueRetryTimer = null;
                    this.processQueue();
                }, 120);
            }
            return;
        }

        const { soundType, playCount, interval, stopTime, serialList } = this.queue.shift();
        this.currentPlaying = soundType;
        this.isPlaying = true;

        let count = 0;

        if (this.currentTimer) {
            clearTimeout(this.currentTimer);
            this.currentTimer = null;
        }

        const finalize = () => {
            this.isPlaying = false;
            this.currentTimer = null;
            this.lastFinishedType = soundType;
            this.cooldownUntil = Date.now() + (stopTime || 0);

            if (!this.holdNext && this.queue.length > 0) {
                this.processQueue();
            }
        };

        const executePlayback = () => {
            if (count < playCount) {
                const sound = this.audioInstances[soundType];
                const activeSerials = serialList.filter(serial => !this.silencedSerial.includes(serial));

                // 중요: 재생 대상이 없어지면 무한대기하지 말고 종료 처리
                if (!sound || activeSerials.length === 0 || this.holdNext) {
                    finalize();
                    return;
                }

                sound.loop = false;
                sound.currentTime = 0;
                sound.play().catch(() => {});
                count++;

                this.currentTimer = setTimeout(executePlayback, interval);
            } else {
                finalize();
            }
        };

        executePlayback();
    }

    addSilencedSerial(serial) {
        if (this.silencedSerial.indexOf(serial) === -1) this.silencedSerial.push(serial)
        // ✅ _updateReminderInterval 직접 호출 제거
        this._scheduleReminderEval();
    }
    
    removeSilencedSerial(serial) {
        this.silencedSerial = this.silencedSerial.filter(item => item !== serial)
        // ✅ _updateReminderInterval 직접 호출 제거
        this._scheduleReminderEval();
    }

    stopCurrent() {
        if (this.currentTimer) {
            clearTimeout(this.currentTimer);
            this.currentTimer = null;
        }
    }

    stopNextQueueCall() {
        // console.log('stopNextQueueCall called')
        this.holdNext = true
    }

    resumeQueueCall() {
        this.holdNext = false;
        if (!this.isPlaying && !this.getAudibleType()) {
            this.processQueue();
        }
    }

    stopAll() {
        this.queue = [];

        if (this.currentTimer) {
            clearTimeout(this.currentTimer);
            this.currentTimer = null;
        }

        if (this.queueRetryTimer) {
            clearTimeout(this.queueRetryTimer);
            this.queueRetryTimer = null;
        }

        ['high', 'med', 'low'].forEach((t) => {
            const a = this.audioInstances[t];
            if (a) {
                a.pause();
                a.currentTime = 0;
            }
        });

        this.isPlaying = false;
        this.currentPlaying = null;
    }

    playReminderOnce() {
        const sound = this.audioInstances['reminder'];
        if (sound) {
            console.log('[Reminder] sound played');
            sound.currentTime = 0;
            sound.loop = false;
            sound.play().catch(err => console.error('Reminder play error:', err));
        }
    }

    // ✅ 외부에서 호출 — 상태만 저장하고 디바운스 평가 예약
    evaluateReminder(playAudio, wardSuspend) {
        this._reminderPlayAudio = playAudio;
        this._reminderWardSuspend = wardSuspend;
        this._scheduleReminderEval();
    }

    // ✅ 디바운스된 평가 예약 (모든 트리거가 이것을 통해 평가)
    _scheduleReminderEval() {
        if (this._reminderEvalTimer) {
            clearTimeout(this._reminderEvalTimer);
        }
        this._reminderEvalTimer = setTimeout(() => {
            this._reminderEvalTimer = null;
            this._updateReminderInterval();
        }, 1500); // 1.5초: 모든 침상의 상태 변경이 안정화될 시간
    }

    _shouldPlayReminder() {
        if (!this._reminderPlayAudio) return false;
        if (this._reminderWardSuspend === "2") return false;
        if (this._reminderWardSuspend === "1") return true;

        // wardSuspend === "0": 재생할 알람이 없고 + 알람 Off 침상이 있을 때
        // allBedsInfo가 비어있으면 아직 초기화 안 된 것 → false
        if (this.allBedsInfo.size === 0) return false;

        const highestPriorityInfo = this.getHighestPriorityAlarmAcrossAllBeds();
        const hasPlayableAlarm = !!highestPriorityInfo;

        let hasInactiveBed = false;
        this.allBedsInfo.forEach((info, serial) => {
            if (!info) return;
            if (info.suspended) {
                hasInactiveBed = true;
                return;
            }
            const data = this.allBedsData.get(serial);
            if (data?.silenceData) {
                const { alarmPause, audioPause } = data.silenceData;
                if (alarmPause === '1' || audioPause === '1') {
                    hasInactiveBed = true;
                }
            }
        });

        return !hasPlayableAlarm && hasInactiveBed;
    }

    _updateReminderInterval() {
        const shouldPlay = this._shouldPlayReminder();

        if (shouldPlay && !this._reminderIntervalId) {
            // this.playReminderOnce();
            this._reminderIntervalId = setInterval(() => {
                if (this._shouldPlayReminder()) {
                    this.playReminderOnce();
                } else {
                    this.stopReminder();
                }
            }, 180000);
        } else if (!shouldPlay && this._reminderIntervalId) {
            this.stopReminder();
        }
        // shouldPlay && this._reminderIntervalId → 이미 돌고 있으므로 아무것도 안 함
        // !shouldPlay && !this._reminderIntervalId → 할 일 없음
    }

    stopReminder() {
        if (this._reminderIntervalId) {
            clearInterval(this._reminderIntervalId);
            this._reminderIntervalId = null;
        }
    }

    // 전역 알람 큐 재설정 (모든 침상 고려)
    resetGlobalAlarmQueue(playAudio, wardSuspend, options = {}) {
        const {
            forceReplayIfIdle = false,
            dedupeKey = null,
            dedupeMs = 1500,
        } = options;

        // force 호출 시 기존 큐/재시도 타이머를 먼저 정리 (문제2 대응)
        if (forceReplayIfIdle) {
            this.queue = [];
            if (this.queueRetryTimer) {
                clearTimeout(this.queueRetryTimer);
                this.queueRetryTimer = null;
            }
        }

        // 같은 이벤트(예: wardSuspend 변경)로 침상 수만큼 호출되는 것 방지
        if (forceReplayIfIdle && dedupeKey) {
            const now = Date.now();
            if (
                this.lastForceReplay.key === dedupeKey &&
                now - this.lastForceReplay.at < dedupeMs
            ) {
                return;
            }
            this.lastForceReplay = { key: dedupeKey, at: now };
        }

        if (this.holdNext && !forceReplayIfIdle) return;

        if (!playAudio) {
            this.stopAll();
            this.clearCooldown();
            return;
        }

        // 기존 동작 유지: 일반 호출에서는 wardSuspend !== '0' 차단
        // 단, 강제 호출(force)일 때는 통과
        if (wardSuspend !== '0' && !forceReplayIfIdle) return;

        const audibleType = this.getAudibleType();
        const isBusyNow = this.isPlaying || !!audibleType;
        const hasTrackedBeds = this.allBedsAlarms.size > 0; // 전환 과도기(0개) 구분

        const highestPriorityInfo = this.getHighestPriorityAlarmAcrossAllBeds();
        if (!highestPriorityInfo) {
            this.queue = [];
            if (!isBusyNow && hasTrackedBeds) this.clearCooldown();
            return;
        }

        const { alarm, serials } = highestPriorityInfo;
        const mappedType = this.mapAlarmType(alarm.type);
        if (mappedType === 'normal') {
            this.queue = [];
            if (!isBusyNow && hasTrackedBeds) this.clearCooldown();
            return;
        }

        const config = this.getPlaybackConfig(mappedType);
        if (!config) return;

        const filteredSerials = serials.filter(
            serial => !(this.silenceRemainingMap.get(serial) > 0)
        );
        if (filteredSerials.length === 0) {
            this.queue = [];
            if (!isBusyNow && hasTrackedBeds) this.clearCooldown();
            return;
        }

        const nextItem = {
            soundType: mappedType,
            playCount: config.playCount,
            interval: config.interval,
            stopTime: config.stopTime,
            serialList: filteredSerials,
        };

        if (audibleType) {
            if (audibleType === mappedType && !forceReplayIfIdle) return;
            this.queue = [nextItem];
            return;
        }

        if (
            !this.isPlaying &&
            this.lastFinishedType === mappedType &&
            Date.now() < this.cooldownUntil &&
            !forceReplayIfIdle
        ) {
            return;
        }

        if (this.isPlaying) {
            if (this.currentPlaying === mappedType && !forceReplayIfIdle) return;
            this.queue = [nextItem];
            return;
        }

        this.queue = [nextItem];
        this.processQueue();
    }

    mapAlarmType(type) {
        switch (type) {
            case 'High':
                return 'high';
            case 'Medium':
                return 'med';
            case 'Low':
                return 'low';
            case 'Normal':
                return 'normal';
            default:
                return 'normal';
        }
    }
}

let globalAlarmSoundManager = null;
export function getGlobalAlarmSoundManager() {
    if (!globalAlarmSoundManager) {
        globalAlarmSoundManager = new AlarmSoundManager();
    }
    return globalAlarmSoundManager;
}

const useAlarm = (bedData, bedInfo) => {
    const [alarmActive, setAlarmActive] = useRecoilState(alarmStateFamily(bedInfo.serial));
    const [alarms, setAlarms] = useRecoilState(alarmsState);
    const [isSilenced, setIsSilenced] = useState(false);
    const [silenceTimeout, setSilenceTimeout] = useState(null);
    const [playAudio] = useRecoilState(audioPlayState); // '소리재생' 버튼 상태
    const [isAlarmEnabled, setIsAlarmEnabled] = useState(true);
    const [alarmPriorities, setAlarmPriorities] = useState({});
    const flattenArray = (arr) => (arr ? arr.flat(Infinity) : []);
    const [currentAlarmType, setCurrentAlarmType] = useState(null);
    const [outerBedInfo, setBedInfo] = useRecoilState(bedInfoState)
    const alarmSoundManager = getGlobalAlarmSoundManager();
    const [remainingSeconds, setRemainingSeconds] = useState(120);
    const [silenceInfo, setSilenceInfo] = useRecoilState(silenceStateFamily(bedInfo.serial));
    const [acknowledgeInfo, setAcknowledgeInfo] = useRecoilState(acknowledgeStateFamily(bedInfo.serial));
    const [acknowledgeRemainingSeconds, setAcknowledgeRemainingSeconds] = useState(120);
    const bedDataRef = useRef(bedData)

    const [alarmState, setAlarmState] = useRecoilState(globalAlarmState);
    // 병동 알람 상태(On=0, Suspend=1, Silence=2)
    const [wardSuspend, ] = useRecoilState(wardSuspendState)
    const bedInfoRef = useRef(bedInfo)
    const prevWardSuspendRef = useRef(wardSuspend);
    const didRunAlarmSyncRef = useRef(false);
    const prevAlarmKeyRef = useRef(null);
    const cooldownTimerRef = useRef(null);
    const priorityOrder = { High: 1, Medium: 2, Low: 3, Normal: 4 };
    
    // const hasUnacknowledgedAlarms = useMemo(() => {
    //     const currentAlarms = alarms[bedInfo.serial] || [];
    //     return currentAlarms.some(alarm => 
    //         !alarmSoundManager.isAlarmAcknowledged(bedInfo.serial, alarm.id)
    //     );
    // }, [alarms[bedInfo.serial], alarmSoundManager.acknowledgedAlarms]);

    // const unacknowledgedAlarmsCount = useMemo(() => {
    //     const currentAlarms = alarms[bedInfo.serial] || [];
    //     return currentAlarms.filter(alarm => 
    //         !alarmSoundManager.isAlarmAcknowledged(bedInfo.serial, alarm.id)
    //     ).length;
    // }, [alarms[bedInfo.serial], alarmSoundManager.acknowledgedAlarms]);

    const hasUnacknowledgedAlarms = useMemo(() => {
        const currentAlarms = alarms[bedInfo.serial] || [];
        // priority 0,1,2 (visual alarm)만 체크
        return currentAlarms.some(alarm => 
            [0, 1, 2].includes(alarm.eventPriority) &&
            !alarmSoundManager.isAlarmAcknowledged(bedInfo.serial, alarm.id)
        );
    }, [alarms[bedInfo.serial], alarmSoundManager.acknowledgedAlarms]);

    const unacknowledgedAlarmsCount = useMemo(() => {
        const currentAlarms = alarms[bedInfo.serial] || [];
        // priority 0,1,2 (visual alarm)만 대상
        return currentAlarms.filter(alarm => 
            [0, 1, 2].includes(alarm.eventPriority) &&
            !alarmSoundManager.isAlarmAcknowledged(bedInfo.serial, alarm.id)
        ).length;
    }, [alarms[bedInfo.serial], alarmSoundManager.acknowledgedAlarms]);

    // Infomative Alarm은 제외 하는 로직을 UseMemo로 변경
    const isAcknowledged = useMemo(() => {
        const currentAlarms = alarms[bedInfo.serial] || [];
        const ackInfo = alarmSoundManager.acknowledgedAlarms.get(bedInfo.serial);
        if (!ackInfo || ackInfo.alarmIds.size === 0) return false;
        // priority 0,1,2인 알람 중 acknowledged된 것이 있는지만 체크
        return currentAlarms.some(alarm => 
            [0, 1, 2].includes(alarm.eventPriority) &&
            ackInfo.alarmIds.has(alarm.id)
        );
    }, [alarms[bedInfo.serial], alarmSoundManager.acknowledgedAlarms]);

    const highestUnacknowledgedPriority = useMemo(() => {
        const currentAlarms = alarms[bedInfo.serial] || [];
        const unacknowledgedAlarms = currentAlarms.filter(alarm => 
            !alarmSoundManager.isAlarmAcknowledged(bedInfo.serial, alarm.id)
        );
        
        if (unacknowledgedAlarms.length === 0) return null;
        
        // 가장 높은 우선순위 찾기 (낮은 숫자가 높은 우선순위)
        const highestPriorityAlarm = unacknowledgedAlarms.reduce((prev, curr) =>
            priorityOrder[curr.type] < priorityOrder[prev.type] ? curr : prev,
            unacknowledgedAlarms[0]
        );
        
        return highestPriorityAlarm.type;
    }, [alarms[bedInfo.serial], alarmSoundManager.acknowledgedAlarms]);

    // acknowledge된 알람 정보를 가져오는 함수
    // const getAlarmAcknowledgeStatus = (alarmId) => {
    //     return alarmSoundManager.isAlarmAcknowledged(bedInfo.serial, alarmId);
    // };
    const getAlarmAcknowledgeStatus = (alarmId, eventPriority) => {
        // priority 0,1,2만 acknowledge 상태 반환
        if (![0, 1, 2].includes(eventPriority)) return false;
        return alarmSoundManager.isAlarmAcknowledged(bedInfo.serial, alarmId);
    };

    // 현재 침상의 acknowledge 시간 가져오기
    const getAcknowledgedTime = () => {
        return alarmSoundManager.getAcknowledgeStartTime(bedInfo.serial);
    };

    const mapAlarmType = (type) => {
        switch (type) {
            case 'High':
                return 'high';
            case 'Medium':
                return 'med';
            case 'Low':
                return 'low';
            case 'Normal':
                return 'normal';
            default:
                return 'normal';
        }
    }

    // 이제 사용하지 않는 함수 (전역 resetGlobalAlarmQueue 사용)
    const resetAlarmQueue = (options = {}) => {
        alarmSoundManager.resetGlobalAlarmQueue(playAudio, wardSuspend, options);
    }

    useEffect(() => {
        if (bedInfo && bedInfo.serial && bedInfo.suspended !== undefined) {
            const alarmActive = !bedInfo.suspended && !bedInfo.silence;
            setAlarmActive(prevState => {
                if (prevState.alarmActive === alarmActive) return prevState;
                return {
                    ...prevState,
                    alarmActive,
                };
            });
            
            if (bedInfo.silence) {
                alarmSoundManager.addSilencedSerial(bedInfo.serial)
            }

            if (!alarmActive) {
                alarmSoundManager.stopNextQueueCall();
            } else {
                if (alarmSoundManager.holdNext) {
                    const anyBedInactive = Array.from(alarmSoundManager.allBedsInfo.entries()).some(
                        ([serial, info]) => {
                            if (!info) return false;
                            if (info.suspended || info.silence) return true;
                            const data = alarmSoundManager.allBedsData.get(serial);
                            if (data?.silenceData) {
                                const { silenceMode, alarmPause, audioPause } = data.silenceData;
                                if (silenceMode === '1' || alarmPause === '1' || audioPause === '1') return true;
                            }
                            return false;
                        }
                    );

                    if (!anyBedInactive) {
                        alarmSoundManager.resumeQueueCall();
                        resetAlarmQueue();
                    }
                }
            }
        }

        if (silenceInfo.isSilenced && silenceInfo.silenceStartTime) {
            const elapsed = Date.now() - silenceInfo.silenceStartTime;
            const remain = Math.max(0, Math.ceil((silenceInfo.duration - elapsed) / 1000));
            setRemainingSeconds(remain);
            alarmSoundManager.updateSilenceRemaining(bedInfo.serial, remain);
        }
        bedInfoRef.current = bedInfo

        // ✅ bedInfo(suspended 등) 변경 시 Reminder 재평가
        alarmSoundManager._scheduleReminderEval();
    }, [bedInfo]);

    useEffect(() => {
        bedDataRef.current = bedData
    }, [bedData])

    useEffect(() => {
        if (!bedData?.silenceData) return;
        const alarmActive = bedData.silenceData.silenceMode === '0' && bedData.silenceData.alarmPause === '0' && bedData.silenceData.audioPause === '0';
        setAlarmActive(prevState => {
            if (prevState.alarmActive === alarmActive) return prevState;
            return {
                ...prevState,
                alarmActive,
            };
        });
        // SilenceMode 처리
        if (bedData.silenceData.silenceMode === '1') {
            alarmSoundManager.addSilencedSerial(bedInfo.serial);
        } else {
            alarmSoundManager.removeSilencedSerial(bedInfo.serial);
        }
        
        if (!alarmActive) {
            alarmSoundManager.stopNextQueueCall();
        } else {
            if (alarmSoundManager.holdNext) {
                // ✅ 동일한 전역 체크: 모든 침상이 활성일 때만 resume
                const anyBedInactive = Array.from(alarmSoundManager.allBedsInfo.entries()).some(
                    ([serial, info]) => {
                        if (!info) return false;
                        const data = alarmSoundManager.allBedsData.get(serial);
                        if (info.suspended || info.silence) return true;
                        if (data?.silenceData) {
                            const { silenceMode, alarmPause, audioPause } = data.silenceData;
                            if (silenceMode === '1' || alarmPause === '1' || audioPause === '1') return true;
                        }
                        return false;
                    }
                );

                if (!anyBedInactive) {
                    alarmSoundManager.resumeQueueCall();
                    resetAlarmQueue();
                }
            }
        }
        
    }, [bedData?.silenceData?.alarmPause, bedData?.silenceData?.audioPause, bedData?.silenceData?.silenceMode]);

    // 병동 Silence 상태 관리
    useEffect(() => {
        const prevWardSuspend = prevWardSuspendRef.current;
        if (prevWardSuspend === wardSuspend) return;
        prevWardSuspendRef.current = wardSuspend;

        if (wardSuspend !== '0') {
            alarmSoundManager.stopNextQueueCall();
        } else {
            alarmSoundManager.resumeQueueCall();

            if (prevWardSuspend !== '0') {
                resetAlarmQueue({
                    forceReplayIfIdle: true,
                    dedupeKey: `wardSuspend:${prevWardSuspend}->${wardSuspend}`,
                    dedupeMs: 1500,
                });
            } else {
                resetAlarmQueue();
            }
        }
        // ✅ evaluateReminder로 상태 전달 (디바운스됨)
        alarmSoundManager.evaluateReminder(playAudio, wardSuspend);
    }, [wardSuspend]);

    // 개별 침상 Silence 타이머 관리
    useEffect(() => {
        if (silenceInfo.isSilenced && silenceInfo.silenceStartTime) {
            const timerId = setInterval(() => {
                const elapsed = Date.now() - silenceInfo.silenceStartTime;
                const remain = Math.max(0, Math.ceil((silenceInfo.duration - elapsed) / 1000));
                setRemainingSeconds(remain);
                alarmSoundManager.updateSilenceRemaining(bedInfo.serial, remain); // Rodney 추가
    
                if (remain <= 0) {
                    //clearInterval(timerId);
                    // axios.patch(`/device/${bedInfo.deviceId}/silence`, { silence: false })
                    //     .then(() => {
                    //         alarmSoundManager.removeSilencedSerial(bedInfo.serial);
                    //         setSilenceInfo({
                    //             ...silenceInfo,
                    //             isSilenced: false,
                    //             silenceStartTime: null,
                    //         });
                    //         setAlarmActive(prev => ({
                    //             ...prev,
                    //             alarmActive: true,
                    //             alarmPause: "0",
                    //             audioPause: "1",
                    //         }));
                    //         handleBedInfoUnSilence()
                    //         if ((bedInfoRef.current.serial && !bedInfoRef.current.suspended)) {
                    //             alarmSoundManager.resumeQueueCall();
                    //             resetAlarmQueue()
                    //         }
                    //     })
                    //     .catch(err => { console.warn(err) })

                    // Rodney - 조나단이랑 이야기 하여 정리함. 타이머가 끝나면 장비랑 동기화 안하는 로직으로 수정.
                    clearInterval(timerId);

                    alarmSoundManager.removeSilencedSerial(bedInfo.serial);
                    setSilenceInfo({
                        ...silenceInfo,
                        isSilenced: false,
                        silenceStartTime: null,
                    });
                    setAlarmActive(prev => ({
                        ...prev,
                        alarmActive: true,
                        alarmPause: "0",
                        audioPause: "1",
                    }));
                    handleBedInfoUnSilence()
                    if ((bedInfoRef.current.serial && !bedInfoRef.current.suspended)) {
                        alarmSoundManager.resumeQueueCall();
                        resetAlarmQueue()
                    }               
                }
            }, 1000);
        
            return () => clearInterval(timerId);
        } else {
            setRemainingSeconds(120);
            alarmSoundManager.updateSilenceRemaining(bedInfo.serial, 0); // Rodney 추가
        }
    }, [
        silenceInfo.isSilenced,
        silenceInfo.silenceStartTime,
        silenceInfo.duration,
        setSilenceInfo,
        setAlarmActive
    ]);
    
    useEffect(() => {
        if (bedData) {
            if (bedData.alarmData) {
                const priorityOrder = { 'High': 1, 'Medium': 2, 'Low': 3, 'Normal': 4 };
                const parsedAlarmMessage = bedData.alarmData.map(item => { return { ...item, type: item.eventPriority, message: item.eventMessage, id: item.id } })

                const alarmList = []
                parsedAlarmMessage.forEach(msgObj => {
                    if (!alarmList.some(alarm => alarm.id === msgObj.id)) {
                        alarmList.push({
                            ...msgObj,
                            type: msgObj.type === 2 ? 'High' : msgObj.type === 1 ? 'Medium' : msgObj.type === 0 ? 'Low' : 'Normal',
                        })
                    }
                })
                // 우선순위에 따라 정렬
                alarmList.sort((a, b) => {
                    const priorityDiff = priorityOrder[a.type] - priorityOrder[b.type];
                    if (priorityDiff !== 0) {
                        return priorityDiff;
                    }
                    const aAcknowledged = alarmSoundManager.isAlarmAcknowledged(bedInfo.serial, a.id);
                    const bAcknowledged = alarmSoundManager.isAlarmAcknowledged(bedInfo.serial, b.id);
                    
                    if (aAcknowledged && !bAcknowledged) return 1;
                    if (!aAcknowledged && bAcknowledged) return -1;
                    return 0;
                });
                
                // 알람 상태 업데이트
                setAlarms(prevAlarms => {
                    const resultAlarms = {
                        ...prevAlarms,
                    }
                    resultAlarms[bedInfo.serial] = alarmList

                    // acknowledged 알람 정리
                    const currentAlarmIds = alarmList.map(alarm => alarm.id);
                    alarmSoundManager.cleanupAcknowledgedAlarms(bedInfo.serial, currentAlarmIds);

                    return resultAlarms
                });
                
                // 전역 알람 상태에 등록
                alarmSoundManager.updateBedAlarms(bedInfo.serial, alarmList, bedInfo, bedData);
                
                // 가장 높은 우선순위 계산
                const highestPriority = alarmList.length > 0 ? alarmList[0].type : 'Normal';
                setAlarmActive(prevState => ({
                    ...prevState,
                    serial: bedInfo.serial,
                    alarmMessage: parsedAlarmMessage,
                    highestPriority,
                }));
            } else {
                setAlarms(prevAlarms => {
                    const resultAlarms = { ...prevAlarms }
                    resultAlarms[bedInfo.serial] = []
                    alarmSoundManager.clearAcknowledged(bedInfo.serial);
                    return resultAlarms
                })
                // 전역 알람 상태에서 제거
                alarmSoundManager.updateBedAlarms(bedInfo.serial, [], bedInfo, bedData);
                setAlarmPriorities({})
            }
        }
    }, [bedData]);

    // 알람 사운드 재생 로직 - 전역으로 관리
    useEffect(() => {
        // mount 직후 첫 실행은 스킵 (컴포넌트 전환 시 불필요 reset 방지)
        if (!didRunAlarmSyncRef.current) {
            didRunAlarmSyncRef.current = true;
            return;
        }

        // 아직 이 침상이 전역 맵에 등록되기 전이면 스킵
        if (!alarmSoundManager.allBedsAlarms.has(bedInfo.serial)) return;

        const currentAlarms = alarms[bedInfo.serial] || [];
        const alarmKey = currentAlarms.map(a => `${a.id}:${a.type}`).join(',');
        const alarmListChanged = prevAlarmKeyRef.current !== alarmKey;
        prevAlarmKeyRef.current = alarmKey;

        // silenceData가 없는 장비(V20)는 silence 조건을 통과시킴
        const silenceData = bedDataRef.current?.silenceData;
        const isSilenceActive = silenceData 
            ? (silenceData.alarmPause !== '0' || silenceData.audioPause !== '0' || silenceData.silenceMode !== '0')
            : false;

        if (
            alarmSoundManager.holdNext && 
            !bedInfoRef.current.suspended &&
            alarmSoundManager.silencedSerial.indexOf(bedInfoRef.current.serial) === -1 &&
            wardSuspend === '0' &&
            !isSilenceActive) {
            
                alarmSoundManager.resumeQueueCall()
                resetAlarmQueue()
        }
        if (!alarmSoundManager.holdNext) {
            if (alarmListChanged || currentAlarms.length > 0) {
                resetAlarmQueue()
            }
        }

        if (cooldownTimerRef.current) {
            clearTimeout(cooldownTimerRef.current);
            cooldownTimerRef.current = null;
        }

        if (
            currentAlarms.length > 0 &&
            !alarmSoundManager.holdNext &&
            !alarmSoundManager.isPlaying &&
            !alarmSoundManager.getAudibleType() &&
            alarmSoundManager.cooldownUntil > Date.now()
        ) {
            const remaining = alarmSoundManager.cooldownUntil - Date.now() + 100; // 100ms 여유
            cooldownTimerRef.current = setTimeout(() => {
                cooldownTimerRef.current = null;
                if (!alarmSoundManager.holdNext && !alarmSoundManager.isPlaying) {
                    resetAlarmQueue();
                }
            }, remaining);
        }

        return () => {
            if (cooldownTimerRef.current) {
                clearTimeout(cooldownTimerRef.current);
                cooldownTimerRef.current = null;
            }
        };
    }, [alarms[bedInfo.serial], playAudio, silenceInfo.isSilenced, alarmActive.alarmActive, bedInfo.suspended, alarmSoundManager]);
    

    // *** 알람 비활성 시 ReminderSound 3분마다 재생 ***
    useEffect(() => {
        // ✅ 상태만 전달 — 내부에서 디바운스 처리
        alarmSoundManager.evaluateReminder(playAudio, wardSuspend);
    }, [playAudio, wardSuspend]);

    // acknowledge 타이머 관리 (침상별)
    useEffect(() => {
        const acknowledgeStartTime = alarmSoundManager.getAcknowledgeStartTime(bedInfo.serial);
        
        if (acknowledgeStartTime) {
            const elapsed = Date.now() - acknowledgeStartTime;
            const remainingTime = Math.max(0, 120000 - elapsed);
            
            if (remainingTime <= 0) {
                alarmSoundManager.clearAcknowledged(bedInfo.serial);
                setAcknowledgeRemainingSeconds(0);
                resetAlarmQueue();
                return;
            }

            setAcknowledgeRemainingSeconds(Math.ceil(remainingTime / 1000));

            const timerId = setInterval(() => {
                const currentElapsed = Date.now() - acknowledgeStartTime;
                const remain = Math.max(0, Math.ceil((120000 - currentElapsed) / 1000));
                setAcknowledgeRemainingSeconds(remain);
                
                if (remain <= 0) {
                    clearInterval(timerId);
                    alarmSoundManager.clearAcknowledged(bedInfo.serial);
                    setAcknowledgeRemainingSeconds(0);
                    resetAlarmQueue();
                }
            }, 1000);

            return () => clearInterval(timerId);
        } else {
            setAcknowledgeRemainingSeconds(0);
        }
    }, [alarmSoundManager.getAcknowledgeStartTime(bedInfo.serial), bedInfo.serial]);

    // acknowledge 처리 함수
    const handleAcknowledge = async () => {
        try {
            const currentAlarms = alarms[bedInfo.serial] || [];
            if (currentAlarms.length === 0) return;

            const currentAlarmIds = currentAlarms.map(alarm => alarm.id);
            alarmSoundManager.acknowledgeCurrentAlarms(bedInfo.serial, currentAlarmIds);
            alarmSoundManager.stopNextQueueCall();
            
            // 전역 큐 재설정 (다른 침상의 알람 확인)
            setTimeout(() => {
                alarmSoundManager.resumeQueueCall();
                resetAlarmQueue();
            }, 100);
        } catch (err) {
            console.warn('Acknowledge failed:', err);
        }
    };

    const handleSilence = async () => {
        try {
            const res = await axios.patch(`/device/${bedInfo.deviceId}/silence`, { silence: true })
            if (res.code != '200') throw res
            alarmSoundManager.addSilencedSerial(bedInfo.serial)
            setSilenceInfo({
                isSilenced: true,
                silenceStartTime: Date.now(),
                duration: 120000,
                remainingSeconds: null,
            });
            setAlarmActive((prev) => ({
                ...prev,
                alarmPause: '0',
            }));
            alarmSoundManager.stopNextQueueCall()
            
            // 전역 큐 재설정
            setTimeout(() => {
                alarmSoundManager.resumeQueueCall();
                resetAlarmQueue();
            }, 100);
        } catch (err) {
            console.warn(err)
        }
    };

    // 개별 침상 Silence 해제 함수
    const handleUnsilence = async () => {
        try {            
            //Rodney - 장비에서 silenceMode가 1인 경우에만 API 호출
            if (bedInfo.deviceModel === 'V20' || (bedInfo.deviceModel !== 'V20' && bedDataRef.current?.silenceData?.silenceMode === '1')) {
                const res = await axios.patch(`/device/${bedInfo.deviceId}/silence`, { silence: false })
                if (res.code != '200') throw res
            }
            
            alarmSoundManager.removeSilencedSerial(bedInfo.serial)
            setSilenceInfo(prev => ({
                ...prev,
                isSilenced: false,
                silenceStartTime: null,
            }));
            setAlarmActive(prev => ({
                ...prev,
                alarmActive: true,
            }));
            handleBedInfoUnSilence()
            alarmSoundManager.resumeQueueCall()
            resetAlarmQueue()
        } catch (err) {
            console.warn(err)
        }
    };

    const handleBedInfoUnSilence = () => {
        setBedInfo((prevBedInfo) =>
            prevBedInfo.map((column) =>
                column.map((bed) => {
                    return {
                        ...bed,
                        silence: false,
                    }
                })
            )
        );

        const isHalf = localStorage.getItem('cnt') || false
        if (isHalf) {
            const oldFullDashboardBedInfo = JSON.parse(localStorage.getItem('bedInfo'))
            if (oldFullDashboardBedInfo) {
                const newFullDashboardBedInfo = oldFullDashboardBedInfo.map((group) => {
                    return group.map((item) => {
                        return {
                            ...item,
                            silence: false,
                        };
                    });
                });
                localStorage.setItem('bedInfo', JSON.stringify(newFullDashboardBedInfo))
            }
        }
    }

    const unSilenceAll = () => {
        // 빈 함수 유지
    }

    // Rodney - 연결 해제 시 알람 상태 초기화
    const clearAlarmState = () => {
        alarmSoundManager.clearAcknowledged(bedInfo.serial);
        alarmSoundManager.removeSilencedSerial(bedInfo.serial);
        alarmSoundManager.updateSilenceRemaining(bedInfo.serial, 0);
        alarmSoundManager.updateBedAlarms(bedInfo.serial, [], bedInfo, bedData);

        setAlarms(prevAlarms => {
            const resultAlarms = { ...prevAlarms };
            resultAlarms[bedInfo.serial] = [];
            return resultAlarms;
        });

        setAlarmPriorities({});
        setAcknowledgeRemainingSeconds(0);
        setRemainingSeconds(120);
        setSilenceInfo(prev => ({
            ...prev,
            isSilenced: false,
            silenceStartTime: null,
        }));

        resetAlarmQueue();
    };

    // 컴포넌트 언마운트 시 정리
    useEffect(() => {
        return () => {
            // 침상 정보 제거
            alarmSoundManager.removeBedAlarms(bedInfo.serial);
        };
    }, []);

    return {
        alarms,
        alarmActive,
        isAlarmEnabled,
        isSilenced: silenceInfo.isSilenced,
        handleSilence,
        handleUnsilence,
        alarmPriorities,
        remainingSeconds,
        unSilenceAll,
        handleAcknowledge,
        acknowledgeRemainingSeconds,
        //isAcknowledged: alarmSoundManager.hasAcknowledgedAlarms(bedInfo.serial),
        isAcknowledged,
        hasUnacknowledgedAlarms,
        unacknowledgedAlarmsCount,
        highestUnacknowledgedPriority,
        getAlarmAcknowledgeStatus,
        getAcknowledgedTime,
        clearAlarmState
    };
};

export default useAlarm;
